import { SlotFormat, SlotLayoutDraft } from 'editor/src/store/design/types';

import { PopupState } from './module/popup/types';

export interface AppState {
  dropdowns: DropdownsState;
  simpleDropdown: SimpleDropdownState;
  customerDeviceType: DeviceType;
  activeActionBar?: ActionBarNames;
  popup: PopupState;
  activeLoaders: { [key in LoaderType]?: boolean };
  mobileView: MobileView;
  showRealisticPreview: boolean;
  /**
   * GAPI-30848 multi-technology editor mode.
   * When true, the global Technology toggle is hidden and the bottom
   * thumbnail strip renders all spreads with per-area tech badges.
   * Set by the parent (storefronts) via the `setMultiTechProductData`
   * post-message.
   */
  multiTechMode?: boolean;
  /**
   * GAPI-30848: per-area technology grouping. Each entry represents one
   * physical print area (canonical name) and the techs available for it.
   * Set via the `setMultiTechAreaGroups` post-message.
   */
  multiTechAreaGroups?: MultiTechAreaGroup[];
  /**
   * GAPI-30848: which technology is currently active for each canonical
   * area. Defaults to the first option in each group; updated when the
   * customer switches via the per-area picker.
   */
  selectedTechByArea?: Record<string, string>;
  /** The product-layout builder on the Personalize tab, while it is open. */
  slotLayoutBuilder?: SlotLayoutBuilderState;
}

export interface SlotLayoutBuilderState {
  spreadIndex: number;
  /** Set while an existing layout's grid is being edited; the other draft fields are then fixed. */
  layoutId?: string;
  draft: SlotLayoutDraft;
  /** Which picture-set folder the host's library is being opened for. */
  folderPick?: { fileSetId: string; columns: SlotFormat };
}

export interface MultiTechAreaGroup {
  canonicalName: string;
  displayName: string;
  /**
   * Every technology the product offers for the area. `disabled` options are
   * shown but not selectable — the product cannot combine them with what is
   * already designed on the other areas.
   */
  options: Array<{ technology: string; spreadName: string; disabled?: boolean }>;
}

export type MobileView = 'spreads' | 'canvas';

export enum ActionBarNames {
  FontFamilies = 'FontFamilies',
  FontColors = 'FontColors',
  ReplaceImage = 'ReplaceImage',
}

export enum TopMenuDropdown {
  Position = 'Position',
  ProductFitting = 'ProductFitting',
  ImageEffects = 'ImageEffects',
  FontSize = 'FontSize',
  TextAlign = 'TextAlign',
  TextSpacing = 'TextSpacing',
  TextFitting = 'TextFitting',
  CropZoom = 'CropZoom',
  Personalization = 'Personalization',
  SVGColor = 'SVGColor',
  LineStyles = 'LineStyles',
  RectangleStyles = 'RectangleStyles',
  LineEdge1 = 'LineEdge1',
  LineEdge2 = 'LineEdge2',
  ThreadColor = 'ThreadColor',
  ProductSwitcher = 'ProductSwitcher',
  ImageInfo = 'ImageInfo',
  ImageOpacity = 'ImageOpacity',
}

export enum DeviceType {
  Mobile = 'Mobile',
  Tablet = 'Tablet',
  Desktop = 'Desktop',
}

export enum LoaderType {
  EditorArea = 'EditorArea',
  ProductPersonalizerPreview = 'ProductPersonalizerPreview',
  ColorSpaceSwitch = 'ColorSpaceSwitch',
}

export interface DropdownsState {
  activeAddLayerDropdown?: string;
  activeTopMenuDropdown?: TopMenuDropdown;
  mobileMoreMenuDropdown?: string;
}

export interface SimpleDropdownState {
  name?: string | null;
  data?: any;
}

export interface AuthHeader {
  [key: string]: string;
}

export declare type TextEffectControl = 'shadow' | 'outline' | 'pattern' | 'curve';
